import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import { TrendingUp, Check, X, Calendar } from 'lucide-react';
import { BACKEND_URL } from '../utils/config';

const API = `${BACKEND_URL}/api`;

const BudgetForecast = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedYear, setSelectedYear] = useState(2025); // Default to 2025 for 2026 budget
  const [budgetForecasts, setBudgetForecasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingValues, setEditingValues] = useState({});

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany && selectedYear) {
      fetchBudgetForecasts();
    }
  }, [selectedCompany, selectedYear]);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data || []);
      
      if (response.data && response.data.length > 0 && !selectedCompany) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgetForecasts = async () => {
    if (!selectedCompany) return;
    
    const nextYear = selectedYear + 1;
    setLoading(true);
    
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/budget-forecasts/${selectedCompany}/${nextYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBudgetForecasts(response.data.forecasts || []);
      
      // Initialize editing values with current forecasts (rounded to 2 decimals)
      const initialValues = {};
      (response.data.forecasts || []).forEach(forecast => {
        initialValues[forecast.budget_line_id] = parseFloat(forecast.forecast_amount.toFixed(2));
      });
      setEditingValues(initialValues);
      
    } catch (error) {
      console.error('Error fetching budget forecasts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (budgetLineId, value) => {
    // Round to 2 decimals if it's a valid number
    const numValue = parseFloat(value);
    const roundedValue = !isNaN(numValue) ? parseFloat(numValue.toFixed(2)) : value;
    
    setEditingValues({
      ...editingValues,
      [budgetLineId]: roundedValue
    });
  };

  const saveForecast = async (budgetLineId) => {
    const newAmount = parseFloat(editingValues[budgetLineId]);
    
    if (isNaN(newAmount)) {
      alert('Inserisci un valore numerico valido');
      return;
    }
    
    try {
      const token = localStorage.getItem('hw_token');
      const nextYear = selectedYear + 1;
      
      await axios.post(`${API}/budget-forecasts/${selectedCompany}`, {
        budget_line_id: budgetLineId,
        year: nextYear,
        forecast_amount: newAmount
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Previsione salvata con successo!');
      fetchBudgetForecasts();
      
    } catch (error) {
      console.error('Error saving forecast:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante il salvataggio';
      alert(errorMsg);
    }
  };

  const resetForecast = (budgetLineId, originalValue) => {
    setEditingValues({
      ...editingValues,
      [budgetLineId]: originalValue
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getCompanyName = () => {
    const company = companies.find(c => c.id === selectedCompany);
    return company ? company.name : '';
  };

  const calculateTotals = (forecasts, type) => {
    const filtered = forecasts.filter(f => f.budget_line_type === type);
    const previousTotal = filtered.reduce((sum, f) => sum + f.previous_year_actual, 0);
    const budgetTotal = filtered.reduce((sum, f) => sum + parseFloat(editingValues[f.budget_line_id] || f.forecast_amount), 0);
    const difference = budgetTotal - previousTotal;
    const percentageChange = previousTotal > 0 ? ((difference / previousTotal) * 100) : 0;
    
    return { previousTotal, budgetTotal, difference, percentageChange };
  };

  if (loading) {
    return (
      <Layout title="Budget Anno Successivo">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
        </div>
      </Layout>
    );
  }

  const nextYear = selectedYear + 1;
  const revenueTotals = calculateTotals(budgetForecasts, 'revenue');
  const expenseTotals = calculateTotals(budgetForecasts, 'expense');

  return (
    <Layout title="Budget Anno Successivo">
      <div className="space-y-6 fade-in">
        {/* Header */}
        <Card className="p-6 border-0 shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">
                Budget {nextYear} - {getCompanyName()}
              </h2>
              <p className="text-sm text-slate-600">
                Previsioni calcolate automaticamente con +10% sui dati reali {selectedYear}
              </p>
            </div>
            <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg">
              <Calendar className="h-5 w-5 text-blue-600" />
              <span className="font-semibold text-blue-900">Anno Budget: {nextYear}</span>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <Card className="p-4 border-0 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Società</Label>
              <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona società" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(company => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name} ({company.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Anno Base (Dati Reali)</Label>
              <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025].map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button onClick={fetchBudgetForecasts} className="w-full">
                <TrendingUp className="h-4 w-4 mr-2" />
                Aggiorna
              </Button>
            </div>
          </div>
        </Card>

        {/* Info Box */}
        <Card className="p-4 border-l-4 border-blue-500 bg-blue-50">
          <div className="flex items-start gap-3">
            <TrendingUp className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900">Come funziona?</h3>
              <p className="text-sm text-blue-800 mt-1">
                Le previsioni sono calcolate automaticamente con un <strong>+10%</strong> sui dati reali dell'anno {selectedYear}.
                Puoi modificare qualsiasi valore cliccando nel campo e salvando le modifiche.
              </p>
            </div>
          </div>
        </Card>

        {/* Budget Table */}
        {budgetForecasts.length > 0 ? (
          <Card className="p-6 border-0 shadow-lg overflow-x-auto">
            <div className="min-w-full">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-semibold min-w-64">Voce</th>
                    <th className="text-center p-3 font-semibold min-w-40 bg-slate-50">
                      Anno {selectedYear}<br />
                      <span className="text-xs font-normal text-slate-600">(Reale)</span>
                    </th>
                    <th className="text-center p-3 font-semibold min-w-40 bg-blue-50">
                      Budget {nextYear}<br />
                      <span className="text-xs font-normal text-blue-600">(+10% default)</span>
                    </th>
                    <th className="text-center p-3 font-semibold min-w-32 bg-green-50">Variazione</th>
                    <th className="text-center p-3 font-semibold min-w-32">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Revenue Section */}
                  <tr className="bg-green-100 border-b-2">
                    <td colSpan="5" className="p-3 font-semibold text-green-800">
                      ENTRATE
                    </td>
                  </tr>
                  
                  {budgetForecasts
                    .filter(f => f.budget_line_type === 'revenue')
                    .sort((a, b) => a.budget_line_order - b.budget_line_order)
                    .map(forecast => {
                      const currentValue = parseFloat(editingValues[forecast.budget_line_id] || forecast.forecast_amount);
                      const difference = currentValue - forecast.previous_year_actual;
                      const percentageChange = forecast.previous_year_actual > 0 
                        ? ((difference / forecast.previous_year_actual) * 100) 
                        : 0;
                      const hasChanged = currentValue !== forecast.forecast_amount;

                      return (
                        <tr key={forecast.budget_line_id} className="border-b hover:bg-slate-50">
                          <td className="p-3 font-medium">
                            {forecast.budget_line_name}
                            {forecast.is_custom && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                Personalizzato
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-center bg-slate-50">
                            {formatCurrency(forecast.previous_year_actual)}
                          </td>
                          <td className="p-3 text-center bg-blue-50">
                            <Input
                              type="number"
                              step="0.01"
                              value={editingValues[forecast.budget_line_id] || ''}
                              onChange={(e) => handleValueChange(forecast.budget_line_id, e.target.value)}
                              className="text-center font-semibold"
                            />
                          </td>
                          <td className={`p-3 text-center bg-green-50 ${
                            difference >= 0 ? 'text-green-700' : 'text-red-700'
                          }`}>
                            <div className="font-semibold">
                              {difference >= 0 ? '+' : ''}{formatCurrency(difference)}
                            </div>
                            <div className="text-xs">
                              ({percentageChange >= 0 ? '+' : ''}{percentageChange.toFixed(1)}%)
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                size="sm"
                                onClick={() => saveForecast(forecast.budget_line_id)}
                                disabled={!hasChanged}
                                className="h-8 px-3"
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => resetForecast(forecast.budget_line_id, forecast.forecast_amount)}
                                disabled={!hasChanged}
                                className="h-8 px-3"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                  {/* TOTALE ENTRATE */}
                  <tr className="border-b-2 bg-green-200 font-bold">
                    <td className="p-3">TOTALE ENTRATE</td>
                    <td className="p-3 text-center bg-slate-100">
                      {formatCurrency(revenueTotals.previousTotal)}
                    </td>
                    <td className="p-3 text-center bg-blue-100">
                      {formatCurrency(revenueTotals.budgetTotal)}
                    </td>
                    <td className={`p-3 text-center bg-green-100 ${
                      revenueTotals.difference >= 0 ? 'text-green-700' : 'text-red-700'
                    }`}>
                      <div>
                        {revenueTotals.difference >= 0 ? '+' : ''}{formatCurrency(revenueTotals.difference)}
                      </div>
                      <div className="text-xs">
                        ({revenueTotals.percentageChange >= 0 ? '+' : ''}{revenueTotals.percentageChange.toFixed(1)}%)
                      </div>
                    </td>
                    <td></td>
                  </tr>

                  {/* Expense Section */}
                  <tr className="bg-red-100 border-b-2">
                    <td colSpan="5" className="p-3 font-semibold text-red-800">
                      USCITE
                    </td>
                  </tr>
                  
                  {budgetForecasts
                    .filter(f => f.budget_line_type === 'expense')
                    .sort((a, b) => a.budget_line_order - b.budget_line_order)
                    .map(forecast => {
                      const currentValue = parseFloat(editingValues[forecast.budget_line_id] || forecast.forecast_amount);
                      const difference = currentValue - forecast.previous_year_actual;
                      const percentageChange = forecast.previous_year_actual > 0 
                        ? ((difference / forecast.previous_year_actual) * 100) 
                        : 0;
                      const hasChanged = currentValue !== forecast.forecast_amount;

                      return (
                        <tr key={forecast.budget_line_id} className="border-b hover:bg-slate-50">
                          <td className="p-3 font-medium">
                            {forecast.budget_line_name}
                            {forecast.is_custom && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                Personalizzato
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-center bg-slate-50">
                            {formatCurrency(forecast.previous_year_actual)}
                          </td>
                          <td className="p-3 text-center bg-blue-50">
                            <Input
                              type="number"
                              step="0.01"
                              value={editingValues[forecast.budget_line_id] || ''}
                              onChange={(e) => handleValueChange(forecast.budget_line_id, e.target.value)}
                              className="text-center font-semibold"
                            />
                          </td>
                          <td className={`p-3 text-center bg-green-50 ${
                            difference <= 0 ? 'text-green-700' : 'text-red-700'
                          }`}>
                            <div className="font-semibold">
                              {difference >= 0 ? '+' : ''}{formatCurrency(difference)}
                            </div>
                            <div className="text-xs">
                              ({percentageChange >= 0 ? '+' : ''}{percentageChange.toFixed(1)}%)
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                size="sm"
                                onClick={() => saveForecast(forecast.budget_line_id)}
                                disabled={!hasChanged}
                                className="h-8 px-3"
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => resetForecast(forecast.budget_line_id, forecast.forecast_amount)}
                                disabled={!hasChanged}
                                className="h-8 px-3"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                  {/* TOTALE USCITE */}
                  <tr className="border-b-2 bg-red-200 font-bold">
                    <td className="p-3">TOTALE USCITE</td>
                    <td className="p-3 text-center bg-slate-100">
                      {formatCurrency(expenseTotals.previousTotal)}
                    </td>
                    <td className="p-3 text-center bg-blue-100">
                      {formatCurrency(expenseTotals.budgetTotal)}
                    </td>
                    <td className={`p-3 text-center bg-green-100 ${
                      expenseTotals.difference <= 0 ? 'text-green-700' : 'text-red-700'
                    }`}>
                      <div>
                        {expenseTotals.difference >= 0 ? '+' : ''}{formatCurrency(expenseTotals.difference)}
                      </div>
                      <div className="text-xs">
                        ({expenseTotals.percentageChange >= 0 ? '+' : ''}{expenseTotals.percentageChange.toFixed(1)}%)
                      </div>
                    </td>
                    <td></td>
                  </tr>

                  {/* MARGINE */}
                  <tr className="border-b-2 bg-purple-200 font-bold text-lg">
                    <td className="p-3">MARGINE PREVISTO</td>
                    <td className="p-3 text-center bg-slate-100">
                      {formatCurrency(revenueTotals.previousTotal - expenseTotals.previousTotal)}
                    </td>
                    <td className="p-3 text-center bg-blue-100">
                      {formatCurrency(revenueTotals.budgetTotal - expenseTotals.budgetTotal)}
                    </td>
                    <td className={`p-3 text-center bg-purple-100 ${
                      (revenueTotals.budgetTotal - expenseTotals.budgetTotal) >= 
                      (revenueTotals.previousTotal - expenseTotals.previousTotal)
                        ? 'text-green-700' : 'text-red-700'
                    }`}>
                      <div>
                        {formatCurrency(
                          (revenueTotals.budgetTotal - expenseTotals.budgetTotal) - 
                          (revenueTotals.previousTotal - expenseTotals.previousTotal)
                        )}
                      </div>
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        ) : (
          <Card className="p-8 border-0 shadow-lg text-center">
            <p className="text-slate-600">
              Nessuna voce di bilancio trovata per questa società.
              <br />
              Crea prima le voci nel Controllo di Gestione.
            </p>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default BudgetForecast;
